/**
 * RNR's Badge, with the design system's caption treatment and its two extra semantic variants.
 *
 * Secondary is the base so colour stays meaningful — a screen where every badge is coloured has no
 * badge that means anything. `default` takes --accent rather than --primary: --accent is the subtle
 * highlight, --primary is the app's one colour, and a badge is not a primary action.
 *
 * The semantic variants tint their own colour rather than taking a fixed background, so they survive
 * a theme swap. `color-mix` is what the design sheet uses on the web; Tailwind's alpha modifier is
 * the same thing here, and it works on both platforms.
 */
import { TextClassContext } from '@/components/ui/text';
import { cn } from '@/lib/utils';
import { Slot } from '@rn-primitives/slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { Platform, View } from 'react-native';

const badgeVariants = cva(
  cn(
    'border-border group shrink-0 flex-row items-center justify-center gap-1 self-start overflow-hidden rounded-full border px-2 py-0.5',
    Platform.select({
      web: 'focus-visible:border-ring focus-visible:ring-ring/50 w-fit whitespace-nowrap transition-[color,box-shadow] focus-visible:ring-[3px]',
    }),
  ),
  {
    variants: {
      variant: {
        default: 'bg-accent border-transparent',
        secondary: 'bg-secondary border-transparent',
        outline: 'bg-transparent',
        destructive: 'bg-destructive/15 border-transparent',
        success: 'bg-success/15 border-transparent',
        warning: 'bg-warning/15 border-transparent',
      },
    },
    defaultVariants: {
      variant: 'secondary',
    },
  },
);

const badgeTextVariants = cva('text-caption uppercase', {
  variants: {
    variant: {
      default: 'text-accent-foreground',
      secondary: 'text-secondary-foreground',
      outline: 'text-muted-foreground',
      destructive: 'text-destructive',
      success: 'text-success',
      warning: 'text-warning',
    },
  },
  defaultVariants: {
    variant: 'secondary',
  },
});

/** The dot cannot be `currentColor` — React Native has no such thing — so it repeats the variant. */
const badgeDotVariants = cva('size-1.5 rounded-full', {
  variants: {
    variant: {
      default: 'bg-accent-foreground',
      secondary: 'bg-secondary-foreground',
      outline: 'bg-muted-foreground',
      destructive: 'bg-destructive',
      success: 'bg-success',
      warning: 'bg-warning',
    },
  },
  defaultVariants: {
    variant: 'secondary',
  },
});

type BadgeProps = React.ComponentProps<typeof View> &
  React.RefAttributes<View> & {
    asChild?: boolean;
    /** A status that reads at a glance before the word does. */
    dot?: boolean;
  } & VariantProps<typeof badgeVariants>;

function Badge({ className, variant, asChild, dot, children, ...props }: BadgeProps) {
  const Component = asChild ? Slot : View;
  return (
    <TextClassContext.Provider value={badgeTextVariants({ variant })}>
      <Component className={cn(badgeVariants({ variant }), className)} {...props}>
        {dot ? <View className={badgeDotVariants({ variant })} /> : null}
        {children}
      </Component>
    </TextClassContext.Provider>
  );
}

export { Badge, badgeDotVariants, badgeTextVariants, badgeVariants };
export type { BadgeProps };
